/**
 * Impersonation context — owner/admin's "view as another user" session.
 *
 * State persisted in localStorage so a page refresh keeps the same view.
 * api() reads it on every request to attach X-Waypoint-Impersonate header.
 * The /v1/team/view-as/start endpoint logs the session to view_as_sessions
 * (audit). /v1/team/view-as/end closes it.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api } from './api';

interface ImpersonationTarget {
  userId: string;
  email: string;
  sessionId: string;
}

interface ImpersonationContextValue {
  target: ImpersonationTarget | null;
  startViewAs: (userId: string, email: string) => Promise<void>;
  stopViewAs: () => Promise<void>;
}

const Ctx = createContext<ImpersonationContextValue | null>(null);
const STORAGE_KEY = 'waypoint:impersonate';

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<ImpersonationTarget | null>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed?.userId && parsed?.email && parsed?.sessionId) {
        return parsed as ImpersonationTarget;
      }
    } catch { /* noop */ }
    return null;
  });

  useEffect(() => {
    try {
      if (target) localStorage.setItem(STORAGE_KEY, JSON.stringify(target));
      else localStorage.removeItem(STORAGE_KEY);
    } catch { /* noop */ }
    window.dispatchEvent(new CustomEvent('waypoint:impersonate-changed'));
  }, [target]);

  const startViewAs = async (userId: string, email: string) => {
    const res = await api<{ session: { id: string } }>('/v1/team/view-as/start', {
      method: 'POST',
      body: JSON.stringify({ target_user_id: userId }),
    });
    setTarget({ userId, email, sessionId: res.session.id });
  };

  const stopViewAs = async () => {
    if (!target) return;
    try {
      await api('/v1/team/view-as/end', {
        method: 'POST',
        body: JSON.stringify({ session_id: target.sessionId }),
      });
    } finally {
      setTarget(null);
    }
  };

  return (
    <Ctx.Provider value={{ target, startViewAs, stopViewAs }}>
      {children}
    </Ctx.Provider>
  );
}

export function useImpersonation(): ImpersonationContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) return { target: null, startViewAs: async () => {}, stopViewAs: async () => {} };
  return ctx;
}

/** Synchronous read for the api() helper (no React hooks). */
export function readImpersonationTarget(): ImpersonationTarget | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.userId && parsed?.email && parsed?.sessionId) {
      return parsed as ImpersonationTarget;
    }
  } catch { /* noop */ }
  return null;
}
